import PropTypes from 'prop-types';
import { Component } from 'react';
import { Col, ControlLabel, FormGroup, Row } from 'react-bootstrap';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash';

import type { CreateProjectGroupRequest } from '../../../../typings/api';
import type { ProjectGroup } from '../../../../typings/common';
import type { NavigationState } from '../../../redux/navigation';
import NavigationStore from '../../../redux/navigation';
import { window } from '../../../globals';
import { userHasPermissionToViewAnyReleaseGroup } from '../../../helpers/permissions';
import OrganizationsModule from '../../../redux/organizations';
import ProjectGroupsModule from '../../../redux/project_grouping';
import ProjectsModule from '../../../redux/projects';
import * as ProjectsV2Store from '../../../redux/projectsV2';
import type { ReduxStore } from '../../../redux/store';
import type { TeamsStore } from '../../../redux/teams';
import TeamsModule from '../../../redux/teams';
import type {
  APIError,
  AwaitedThunkResponse,
  IAsyncStatus,
  Policy,
  Project,
  ProjectGroupReleaseProject,
  Team,
} from '../../../types';
import { hasError } from '../../../utils';
import { ErrorAlert } from '../../generic/ErrorAlert';
import Button, { ButtonGroup, TooltipButton } from '../../generic/Button';
import Icon, { icons } from '../../generic/Icon';
import Panel from '../../generic/Panel';
import Text from '../../generic/Text';
import SelectTeam from '../SelectTeam';
import PolicySettings from './components/PolicySettings';
import ConfirmProjectGroup from './steps/ConfirmProjectGroup';
import CreateProjectGroup from './steps/CreateProjectGroup';
import Flex from '../../generic/Flex';
import { isProjectValidForRelease } from './helper';
import ProjectGroupTokenSettings from './components/ProjectGroupPortalSettings';
import { shouldDisplayOrganizationPortalTokenSettings } from '../../settings/shared/permissions';
import { commitTokenChanges } from './request';

type Selectable<T> = T & { selected?: boolean };
type SelectableProject = Selectable<Project>;

type TooltipButtonProps = {
  tooltip: string;
  showTooltip: boolean;
  isDisabled: boolean;
};

function tooltipButtonPropsFactory({
  tooltip = '',
  showTooltip = true,
  isDisabled = true,
}: Partial<TooltipButtonProps>): TooltipButtonProps {
  return {
    tooltip,
    showTooltip,
    isDisabled,
  };
}

type ReduxStateProps = {
  projects: SelectableProject[];
  selectedProjects: SelectableProject[] | 'all';
  projectGroups: ProjectGroup[];
  status: IAsyncStatus<APIError>;
  organizationStatus: IAsyncStatus;
  licensingPolicies: Policy[];
  securityPolicies: Policy[];
  qualityPolicies: Policy[];
  allTeams: Team[];
  teamUpdatingStatus: TeamsStore['teamUpdatingStatus'];
};

type DispatchProps = {
  fetchProjects: (filter: Record<string, any>) => void;
  selectAllFilteredV2: () => void;
  fetchRevisions: (locator: string, branch: string) => Promise<void>;
  toggleProjectSelect: (locators: string[]) => void;
  clearSelectedProjects: () => void;
  createProjectGroup: (projectGroup: CreateProjectGroupRequest) => Promise<AwaitedThunkResponse<ProjectGroup>>;
  fetchOrganization(id: number): Promise<void>;
  fetchAllTeams: (filters: object) => void;
  addProjectsToTeam: (teamId: string | number, projectIds: string[]) => void;
  clearTeamsErrors: () => void;
  setNavState: (newState: Partial<NavigationState>) => void;
};

type CreateProjectGroupPageProps = ReduxStateProps & DispatchProps;

type CreateProjectGroupPageState = {
  title: string;
  release: string;
  confirming: boolean;
  projects: ProjectGroupReleaseProject[];
  selectedSecurityPolicy?: Policy;
  selectedLicensingPolicy?: Policy;
  selectedQualityPolicy?: Policy;
  selectedTeams: Team[];
  pendingAddTokenIds: number[];
  pendingRemoveTokenIds: number[];
  publicOnPortal: boolean;
};

class CreateProjectGroupPage extends Component<CreateProjectGroupPageProps, CreateProjectGroupPageState> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state = {
    release: '',
    title: '',
    confirming: false,
    // These are initialized undefined because TypeScript was complaining that they didn't exist otherwise.
    projects: [] as ProjectGroupReleaseProject[],
    selectedSecurityPolicy: null,
    selectedLicensingPolicy: null,
    selectedQualityPolicy: null,
    selectedTeams: [] as Team[],
    pendingAddTokenIds: [],
    pendingRemoveTokenIds: [],
    publicOnPortal: false,
  };

  componentDidMount() {
    this.props.clearTeamsErrors();
    this.props.fetchProjects({ organizationId: window.user.organizationId, count: 500 });

    // If "all" projects have been selected, that means we're using Projects V2 and need to prepopulate
    // the selectedProjects with a list of projects that respects the filters.
    if (this.props.selectedProjects === 'all') {
      this.props.selectAllFilteredV2();
    }

    this.props.fetchOrganization(window.user.organizationId);
    this.props.setNavState({ expand: null, subnav: null });
    this.props.fetchAllTeams({ organizationId: window.user.organizationId });
  }

  componentDidUpdate(prevProps: CreateProjectGroupPageProps) {
    if (prevProps.projectGroups.length < this.props.projectGroups.length) {
      this.props.clearSelectedProjects();
      this.context.router.push('/projects');
    }

    // If projects have not been loaded into redux yet, wait until they are and then set
    // the selected projects if they haven't already been set.
    if (
      this.props.projects.length !== 0
        && this.state.projects.length === 0
        && this.props.selectedProjects.length !== 0
        && this.props.selectedProjects !== 'all'
    ) {
      const validProjects = this.props.selectedProjects.filter(isProjectValidForRelease);
      this.setState({
        projects: validProjects.map(project => ({
          // these fields are always present and asserting their types is
          // safer than changing the widely shared Project type
          projectId: project.locator as string,
          branch: project.default_branch as string,
          // select the project's latest revision by default
          revision: project.revisions?.[0],
          project,
        })),
      });
    }
  }

  get requiredTeamNotSelected(): boolean {
    const { confirming, selectedTeams } = this.state;
    return confirming &&
      !userHasPermissionToViewAnyReleaseGroup() &&
      selectedTeams.length === 0;
  }

  //  Return specific tooltip props letting the user know why the button is disabled.
  get tooltipButtonProps(): { tooltip: string; showTooltip: boolean; isDisabled: boolean } {
    const { loading: isLoading } = this.props.status;
    const { title, release, projects } = this.state;

    if (isLoading) {
      //  No tooltip but disable button.
      return tooltipButtonPropsFactory({
        showTooltip: false,
      });
    }

    if (!title.trim().length) {
      return tooltipButtonPropsFactory({ tooltip: 'Please add a title for this group' });
    }

    if (title.length > 255) {
      return tooltipButtonPropsFactory({ tooltip: 'Title must be less than 255 characters' });
    }

    if (!release.trim().length) {
      return tooltipButtonPropsFactory({ tooltip: 'Please add a release for this group' });
    }

    if (release.length > 35) {
      return tooltipButtonPropsFactory({ tooltip: 'Release must be less than 35 characters' });
    }

    if (!projects.length) {
      return tooltipButtonPropsFactory({ tooltip: 'Please add at least one project to this group' });
    }

    if (projects.some(project => !project.revision)) {
      return tooltipButtonPropsFactory({ tooltip: 'Please select a revision for each project' });
    }

    if (this.requiredTeamNotSelected) {
      return tooltipButtonPropsFactory({ tooltip: 'Your team does not have permission to create this group' });
    }

    return tooltipButtonPropsFactory({
      showTooltip: false,
      isDisabled: false,
    });
  }

  onAddProject = (project: Project) => {
    const newProject = {
      // these fields are always present and asserting their types is
      // safer than changing the widely shared Project type
      projectId: project.locator as string,
      locator: project.locator as string,
      branch: project.default_branch as string,
      // select the project's latest revision by default
      revision: project.revisions?.[0],
      project,
    };
    this.setState(state => ({
      projects: state.projects.concat(newProject),
    }));
  };

  onRemoveProject = (projectToRemove: ProjectGroupReleaseProject) => {
    this.setState(state => ({
      projects: state.projects.filter(project => project.projectId !== projectToRemove.projectId),
    }));
  };

  onUpdateProject = (updateProject: ProjectGroupReleaseProject) => {
    this.setState(state => ({
      projects: state.projects.map(project => {
        if (project.projectId === updateProject.projectId) {
          return {
            ...project,
            ...updateProject,
          };
        }
        return project;
      }),
    }));
  };

  onNextStep = async () => {
    if (!this.state.confirming) {
      this.setState({ confirming: true });
      return;
    }

    const { createProjectGroup } = this.props;

    const teamIds: number[] = [];
    if (this.state.selectedTeams.length !== 0) {
      this.state.selectedTeams.forEach(team => teamIds.push(team.id));
    }

    if (teamIds.length) {
      // add the projects to the teams first
      const projectIds: string[] = [];
      this.state.projects.forEach(project => projectIds.push(project.projectId));
      // eslint-disable-next-line no-restricted-syntax
      for (const teamId of teamIds) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await this.props.addProjectsToTeam(teamId, projectIds);
        } catch (err) {
          // Error handled by Redux.
          return; // return in the case there is an error
        }
      }
    }

    const projectGroup = await createProjectGroup({
      title: this.state.title,
      release: {
        projects: this.state.projects.map(project => ({
          // these fields are always present and asserting their types is
          // safer than changing the widely shared Project type
          projectId: project.projectId as string,
          branch: project.branch as string,
          revisionId: project.revision?.locator,
        })),
        title: this.state.release,
      },
      licensingPolicyId: this.state.selectedLicensingPolicy?.id,
      securityPolicyId: this.state.selectedSecurityPolicy?.id,
      qualityPolicyId: this.state.selectedQualityPolicy?.id,
      publicOnPortal: this.state.publicOnPortal,
      teams: teamIds,
    });

    const projectGroupId = projectGroup.value.data?.id;
    if (projectGroupId) {
      await commitTokenChanges(projectGroupId, this.state.pendingAddTokenIds, this.state.pendingRemoveTokenIds);
    }
  };

  onAddToken = (tokenId: number) => {
    this.setState(previousState => ({
      pendingAddTokenIds: _.uniq([...previousState.pendingAddTokenIds, tokenId]),
    }));
  };

  onRemoveToken = (tokenId: number) => {
    this.setState(previousState => ({
      // Remove the uncommitted token from the list of pending adds
      pendingAddTokenIds: previousState.pendingAddTokenIds.filter(id => id !== tokenId),
      pendingRemoveTokenIds: _.uniq([...previousState.pendingRemoveTokenIds, tokenId]),
    }));
  };

  onCancelConfirm = () => {
    if (this.state.confirming) {
      this.setState({ confirming: false });
      return;
    }

    this.props.clearSelectedProjects();
    this.context.router.push('/projects');
  };

  onChangePolicy = (setting) => (option) => {
    this.setState({
      [setting]: option,
    });
  };

  setProjectGroupTitle = (title: string) =>
    this.setState({
      title,
    });

  onTeamsSelected = (selectedTeams: Team[]) =>
    this.setState({ selectedTeams });

  setProjectGroupRelease = (release: string) =>
    this.setState({
      release,
    });

  get projects() {
    return this.props.projects.filter(
      (project) => project.locator
        && !this.state.projects.find(releaseProject => releaseProject.projectId === project.locator),
    );
  }

  render() {
    return (
      <div className="container create-project-group-page-body">
        <Helmet title="Create Release Group" />
        <div className="page-description">
          <Row>
            <Col xs={12} className="text-center fa-5x">
              <Icon icon={icons.folder} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="text-center">
              <Text.H1>Create Release Group</Text.H1>
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="text-center">
              <p>
                Release Groups let you bundle multiple projects together so that you can<br />
                apply a shared policy and track issues across the entire collection.
              </p>
            </Col>
          </Row>
        </div>
        {hasError(this.props.teamUpdatingStatus) && (
          <ErrorAlert status={this.props.teamUpdatingStatus} />
        )}
        {
          (this.props.status && !_.isEmpty(this.props.status.errors)) && (
            <div role="alert" className="alert alert-warning alert-dismissible">
              Failed to create project group: {this.props.status.errors.map(e => e.message).join(', ')}
            </div>
          )
        }
        <Panel className="padded">
          {
            this.state.confirming ? (
              <ConfirmProjectGroup
                release={this.state.release}
                title={this.state.title}
                releaseProjects={this.state.projects}
              />
            ) : (
              <CreateProjectGroup
                projects={this.projects}
                releaseProjects={this.state.projects}
                title={this.state.title}
                setTitle={this.setProjectGroupTitle}
                release={this.state.release}
                setRelease={this.setProjectGroupRelease}
                onRemoveProject={this.onRemoveProject}
                onAddProject={this.onAddProject}
                onUpdateProject={this.onUpdateProject}
              />
            )
          }
          {this.state.confirming && (
            <>
              <PolicySettings
                settingsMode={{ mode: 'creating' }}
                onChangeLicensingPolicy={this.onChangePolicy('selectedLicensingPolicy')}
                onChangeSecurityPolicy={this.onChangePolicy('selectedSecurityPolicy')}
                onChangeQualityPolicy={this.onChangePolicy('selectedQualityPolicy')}
                selectedSecurityPolicy={this.state.selectedSecurityPolicy}
                selectedLicensingPolicy={this.state.selectedLicensingPolicy}
                selectedQualityPolicy={this.state.selectedQualityPolicy}
                licensingPolicies={this.props.licensingPolicies}
                securityPolicies={this.props.securityPolicies}
                qualityPolicies={this.props.qualityPolicies}
                isLoadingPolicyOptions={this.props.organizationStatus.loading}
              />
              <FormGroup>
                <ControlLabel>
                  Assign to Teams
                </ControlLabel>
                <br />
                <Text.Text>Projects in this release group will be added to the selected teams.</Text.Text>
                <SelectTeam
                  allTeams={this.props.allTeams}
                  selectedTeams={this.state.selectedTeams}
                  onTeamsSelected={this.onTeamsSelected}
                  permissionKind="releaseGroup"
                />
              </FormGroup>
              {shouldDisplayOrganizationPortalTokenSettings() && (
                <>
                  <br />
                  <ProjectGroupTokenSettings
                    onAddToken={this.onAddToken}
                    onRemoveToken={this.onRemoveToken}
                    publicOnPortal={this.state.publicOnPortal}
                    setPublicOnPortal={(publicOnPortal) => this.setState({ publicOnPortal })}
                  />
                </>
              )}
            </>
          )}
          <Flex justify="flex-end">
            <ButtonGroup>
              <span id="back-button">
                <Button
                  type="secondary"
                  size="large"
                  onClick={this.onCancelConfirm}
                >
                  {this.state.confirming ? 'Back' : 'Cancel'}
                </Button>
              </span>
              <span id="next-step-button">
                <TooltipButton
                  type="primary"
                  size="large"
                  onClick={this.onNextStep}
                  //  eslint-disable-next-line react/jsx-props-no-spreading
                  {...this.tooltipButtonProps}
                >
                  {this.state.confirming ? 'Create Release Group' : 'Next Step'}
                </TooltipButton>
              </span>
            </ButtonGroup>
          </Flex>
        </Panel>
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  projects: ProjectsModule.selectors.getProjectList,
  selectedProjects: ProjectsModule.selectors.getSelectedProjects,
  status: ProjectGroupsModule.selectors.getStatus,
  projectGroups: ProjectGroupsModule.selectors.getEntityList,
  organizationStatus: OrganizationsModule.selectors.getStatus,
  licensingPolicies: OrganizationsModule.selectors.getCurrentOrganizationLicensingPolicies,
  securityPolicies: OrganizationsModule.selectors.getCurrentOrganizationSecurityPolicies,
  qualityPolicies: OrganizationsModule.selectors.getCurrentOrganizationQualityPolicies,
  allTeams: TeamsModule.selectors.getTeamList,
  teamUpdatingStatus: TeamsModule.selectors.getTeamUpdatingStatus,
});

const mapDispatchToProps: DispatchProps = {
  fetchProjects: ProjectsModule.actions.fetchAll,
  selectAllFilteredV2: ProjectsV2Store.actions.selectAllFilteredV2,
  fetchRevisions: ProjectsModule.actions.fetchRevisions,
  toggleProjectSelect: ProjectsModule.actions.toggleProjectSelect,
  clearSelectedProjects: ProjectsModule.actions.clearSelectedProjects,
  createProjectGroup: ProjectGroupsModule.actions.create,
  fetchOrganization: OrganizationsModule.actions.fetchOne,
  fetchAllTeams: TeamsModule.actions.fetchAll,
  addProjectsToTeam: TeamsModule.actions.addTeamProjects,
  clearTeamsErrors: TeamsModule.actions.clearErrors,
  setNavState: NavigationStore.actions.setNavState,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateProjectGroupPage);
